import React, { useState } from 'react';
import { usePalettePermissions } from '../hooks/usePalettePermissions';
import { getRoleDescription, type PaletteRole } from '../lib/permissions';

interface PalettePermissionsPanelProps {
  paletteId: string;
  onClose?: () => void;
}

export const PalettePermissionsPanel: React.FC<PalettePermissionsPanelProps> = ({
  paletteId,
  onClose,
}) => {
  const {
    access,
    userRole,
    loading,
    error,
    canManagePermissions,
    shareWith,
    revokeAccess,
    makePublic,
  } = usePalettePermissions(paletteId);

  const [shareUserId, setShareUserId] = useState('');
  const [shareRole, setShareRole] = useState<PaletteRole>('viewer');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const handleShare = async () => {
    if (!shareUserId.trim()) {
      setActionError('Please enter a user ID');
      return;
    }

    try {
      setActionError(null);
      setActionSuccess(null);
      await shareWith(shareUserId.trim(), shareRole);
      setActionSuccess(`Successfully shared with user as ${shareRole}`);
      setShareUserId('');
    } catch (err) {
      setActionError(String(err));
    }
  };

  const handleRevoke = async (userId: string) => {
    try {
      setActionError(null);
      setActionSuccess(null);
      await revokeAccess(userId);
      setActionSuccess('Access revoked successfully');
    } catch (err) {
      setActionError(String(err));
    }
  };

  const handleTogglePublic = async () => {
    try {
      setActionError(null);
      setActionSuccess(null);
      await makePublic(!access?.isPublic);
      setActionSuccess(
        access?.isPublic
          ? 'Palette is now private'
          : 'Palette is now public (view-only)'
      );
    } catch (err) {
      setActionError(String(err));
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
        <p className="text-gray-600 dark:text-gray-400">Loading permissions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-red-600 dark:text-red-400">Error: {error}</p>
      </div>
    );
  }

  if (!access) {
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
        <p className="text-yellow-600 dark:text-yellow-400">
          No access information available
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Palette Permissions
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        )}
      </div>

      {/* Current User Role */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
          Your Role: <span className="font-bold capitalize">{userRole || 'None'}</span>
        </p>
        {userRole && (
          <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
            {getRoleDescription(userRole)}
          </p>
        )}
      </div>

      {/* Action Messages */}
      {actionError && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">{actionError}</p>
        </div>
      )}
      {actionSuccess && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
          <p className="text-sm text-green-600 dark:text-green-400">{actionSuccess}</p>
        </div>
      )}

      {/* Owner Information */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Owner
        </h4>
        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
          <p className="text-sm text-gray-900 dark:text-white font-mono">
            {access.ownerId}
          </p>
        </div>
      </div>

      {/* Public Access */}
      {canManagePermissions && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Public Access
          </h4>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
            <div>
              <p className="text-sm text-gray-900 dark:text-white">
                {access.isPublic ? 'Public (Anyone can view)' : 'Private'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {access.isPublic
                  ? 'Anyone with the link can view this palette'
                  : 'Only you and shared users can access'}
              </p>
            </div>
            <button
              onClick={handleTogglePublic}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                access.isPublic
                  ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              {access.isPublic ? 'Make Private' : 'Make Public'}
            </button>
          </div>
        </div>
      )}

      {/* Shared With */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Shared With ({access.sharedWith?.length || 0})
        </h4>
        {access.sharedWith && access.sharedWith.length > 0 ? (
          <div className="space-y-2">
            {access.sharedWith.map((share, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded"
              >
                <div className="flex-1">
                  <p className="text-sm text-gray-900 dark:text-white font-mono">
                    {share.userId}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Role: <span className="capitalize font-medium">{share.role}</span>
                    {' • '}
                    Granted: {new Date(share.grantedAt).toLocaleDateString()}
                  </p>
                </div>
                {canManagePermissions && (
                  <button
                    onClick={() => handleRevoke(share.userId)}
                    className="ml-3 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded transition-colors"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
            Not shared with anyone
          </p>
        )}
      </div>

      {/* Share Form */}
      {canManagePermissions && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Share Palette
          </h4>
          <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700 rounded">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                User ID
              </label>
              <input
                type="text"
                value={shareUserId}
                onChange={(e) => setShareUserId(e.target.value)}
                placeholder="Enter user ID to share with"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                value={shareRole}
                onChange={(e) => setShareRole(e.target.value as PaletteRole)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              >
                <option value="viewer">Viewer (View & Export)</option>
                <option value="editor">Editor (View, Edit & Export)</option>
                <option value="owner">Owner (Full Control)</option>
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getRoleDescription(shareRole)}
              </p>
            </div>
            <button
              onClick={handleShare}
              className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded font-medium transition-colors"
            >
              Share
            </button>
          </div>
        </div>
      )}

      {/* Info for non-managers */}
      {!canManagePermissions && (
        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            You do not have permission to manage access to this palette.
            Contact the owner to request changes.
          </p>
        </div>
      )}
    </div>
  );
};
